import React, { useState, useEffect } from 'react';
import { Select, Button, Modal, Form, Input, Switch, Table, message, Spin, Typography } from 'antd';
import axios from 'axios';
import './Components.css';
import { getEndpoint } from '../utils/endpoints';

const { Option } = Select;

interface Memory {
  description: string;
  direction: string;
  domain: string;
  memory_code: string;
  memory_name: string;
  category: string[];
  reversible: boolean;
  client: string;
}

interface MemoryRow {
  score: number;
  domain: string;
  id: string;
  source: string;
  status: string;
  target: string;
  category: string;
}

const MemoryManagement: React.FC = () => {
  const [memories, setMemories] = useState<Memory[]>([]);
  const [selectedMemory, setSelectedMemory] = useState<string | null>(null);
  const [queryText, setQueryText] = useState<string>('');
  const [memoryData, setMemoryData] = useState<MemoryRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isAddingMemory, setIsAddingMemory] = useState(false);
  const [editingRow, setEditingRow] = useState<MemoryRow | null>(null);
  const [isRowModalVisible, setIsRowModalVisible] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [deleteConfirmation, setDeleteConfirmation] = useState<string>('');
  const [rowForm] = Form.useForm();
  const [deleteRow, setDeleteRow] = useState<MemoryRow | null>(null);

  useEffect(() => {
    fetchMemories();
  }, []);

  const fetchMemories = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${getEndpoint('BACKEND')}/getMemories/`);
      setMemories(response.data);
    } catch (error) {
      console.error('Error fetching memories:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMemorySelect = async (memoryName: string) => {
    setSelectedMemory(memoryName);
  };

  const handleQueryMemory = async () => {
    if (!selectedMemory || !queryText) {
      message.error('Please select a memory and enter a query text.');
      return;
    }
    setLoading(true);
    try {
      const selected = memories.find(memory => memory.memory_name === selectedMemory);
      const payload = {
        memory_code: (selected?.memory_code || selectedMemory || '').toLowerCase(),
        query: queryText,
        n_results: 5,
        include_docs: true,
        dominio: Array.isArray(selected?.domain) ? selected?.domain[0] : selected?.domain || '',
        categoria: selectedCategory,
      };
      const response = await axios.post("https://memories.patren.intellitude.com/query", payload, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      setMemoryData(response.data.matches || []);
    } catch (error) {
      console.error('Error querying memory:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddOrEditRow = async (values: MemoryRow) => {
    if (!selectedMemory) return;
    setLoading(true);
    try {
      const endpoint = `${getEndpoint('MEMORY')}/add/${memories.find(memory => memory.memory_name === selectedMemory)?.client}/${memories.find(memory => memory.memory_name === selectedMemory)?.domain}/${memories.find(memory => memory.memory_name === selectedMemory)?.direction}`;
      const payload = {
        previousValues: editingRow,
        newValues: {
          source: values.source,
          status: values.status,
          target: values.target,
          category: values.category,
          domain: values.domain,
        },
      };
      await axios.post(endpoint, payload);
      message.success(editingRow ? 'Row updated successfully!' : 'Row added successfully!');
      handleQueryMemory();
      setIsRowModalVisible(false);
    } catch (error) {
      console.error('Error saving row:', error);
      message.error('Failed to save row.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRow = async () => {
    if (!selectedMemory || !deleteRow) return;
    setLoading(true);
    try {
      const endpoint = `${getEndpoint('MEMORY')}/delete/${memories.find(memory => memory.memory_name === selectedMemory)?.client}/${memories.find(memory => memory.memory_name === selectedMemory)?.domain}/${memories.find(memory => memory.memory_name === selectedMemory)?.direction}`;
      const payload = {
        source: deleteRow.source,
        status: deleteRow.status,
        target: deleteRow.target,
        category: deleteRow.category,
        id: deleteRow.id, 
      };
      await axios.post(endpoint, payload);
      message.success('Row deleted successfully!');
      handleQueryMemory();
      setIsDeleteModalVisible(false);
    } catch (error) {
      console.error('Error deleting row:', error);
      message.error('Failed to delete row.');
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'Score',
      dataIndex: 'score',
      key: 'score',
      render: (score: number) => score?.toFixed(3),
    },
    {
      title: 'Source',
      dataIndex: 'document',
      key: 'document',
    },
    {
      title: 'Target',
      dataIndex: ['metadata', 'target'],
      key: 'target',
      render: (_: any, record: any) => record.metadata?.target || '',
    },
    {
      title: 'Domain',
      dataIndex: ['metadata', 'domain'],
      key: 'domain',
      render: (_: any, record: any) => record.metadata?.domain || '',
    },
    {
      title: 'Category',
      dataIndex: ['metadata', 'category'],
      key: 'category',
      render: (_: any, record: any) => record.metadata?.category || '',
    },
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
    },
  ];

  // Memoized selected memory object and categories
  const selectedMemoryObj = memories.find(({ memory_name }) => memory_name === selectedMemory);
  const categories = selectedMemoryObj?.category || [];

  // Category dropdown state
  const [selectedCategory, setSelectedCategory] = useState<string>("*");

  return (
    <div>
      <Spin spinning={loading}>
        <div style={{ marginBottom: 16 }}>
          <Select
            showSearch
            placeholder="Select a memory"
            style={{ width: 300 }}
            value={selectedMemory}
            onChange={handleMemorySelect}
          >
            {memories.map(({ memory_name }) => (
              <Option key={memory_name} value={memory_name}>{memory_name}</Option>
            ))}
          </Select>
        </div>
  
        {selectedMemory && (
          <div style={{ marginTop: 10, padding: 10, border: '1px solid #d9d9d9', borderRadius: 4, backgroundColor: '#fafafa' }}>
            {['Description', 'Direction', 'Domain', 'Categories'].map((field) => {
              const value = memories.find(({ memory_name }) => memory_name === selectedMemory)?.[
                field.toLowerCase() === 'categories' ? 'category' : field.toLowerCase()
              ];
              let displayValue = 'N/A';
              if (Array.isArray(value)) {
                displayValue = value.length > 0 ? value.join(', ') : 'N/A';
              } else if (value) {
                displayValue = value;
              }
              return (
                <div key={field}>
                  <Typography.Text strong>{field}: </Typography.Text>
                  <Typography.Text>{displayValue}</Typography.Text>
                </div>
              );
            })}
          </div>
        )}
  
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10, width: 800 }}>
          <Select
            placeholder="Select a category"
            style={{ width: 300 }}
            value={selectedCategory}
            onChange={value => setSelectedCategory(value)}
          >
            <Option key="all-categories" value="*">All categories</Option>
            {categories.map((category) => (
              <Option key={category} value={category}>{category}</Option>
            ))}
          </Select>
          <Input
            placeholder="Enter text to query"
            style={{ width: 300 }}
            value={queryText}
            onChange={(e) => setQueryText(e.target.value)}
          />
          <Button onClick={handleQueryMemory}>Query Memory</Button>
        </div>
  
        {memoryData.length > 0 && (
          <Table
            columns={columns}
            dataSource={memoryData}
            rowKey="id"
            rowClassName={(record) => (record.score < 0.9 ? 'low-score-row' : '')}
          />
        )}
      </Spin>
  
      <Modal title="Edit Row" open={isRowModalVisible} onCancel={() => setIsRowModalVisible(false)} onOk={() => rowForm.submit()}>
        <Form form={rowForm} onFinish={handleAddOrEditRow} initialValues={editingRow || {}}>
          {['Source', 'Target', 'Category', 'Domain'].map((field) => (
            <Form.Item key={field} name={field.toLowerCase()} label={field} rules={[{ required: true }]}> 
              <Input />
            </Form.Item>
          ))}
        </Form>
      </Modal>
  
      {/* Modal for the button "Delete" */}
      <Modal
        title="Delete Row"
        open={isDeleteModalVisible}
        onCancel={() => setIsDeleteModalVisible(false)}
        onOk={handleDeleteRow}
        okButtonProps={{ disabled: deleteConfirmation !== 'delete' }}
      >
        {deleteRow && (
          <div>
        <p><strong>ID:</strong> {deleteRow.id}</p>
        <p><strong>Domain:</strong> {deleteRow.domain}</p>
        <p><strong>Category:</strong> {deleteRow.category}</p>
        <p><strong>Source:</strong> {deleteRow.source}</p>
        <p><strong>Target:</strong> {deleteRow.target}</p>
        <p><strong>Status:</strong> {deleteRow.status}</p>
          </div>
        )}
                <p>Are you sure you want to delete this row? Please type "delete" to confirm.</p>
        <Input
          placeholder="Type 'delete' to confirm"
          value={deleteConfirmation}
          onChange={(e) => setDeleteConfirmation(e.target.value)}
        />

      </Modal>

      <Button type="primary" onClick={() => { setEditingRow(null); setIsRowModalVisible(true); }}>
        Add Row
      </Button>

      <Modal title="Add Row" open={isRowModalVisible} onCancel={() => setIsRowModalVisible(false)} onOk={() => rowForm.submit()}>
        <Form form={rowForm} onFinish={handleAddOrEditRow} initialValues={editingRow || {}}>
          {['Source', 'Target', 'Category', 'Domain'].map((field) => (
        <Form.Item key={field} name={field.toLowerCase()} label={field} rules={[{ required: true }]}>
          <Input />
        </Form.Item>
          ))}
        </Form>
      </Modal>

    </div>
  );
}

export default MemoryManagement;
